import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Reveal, Chapter } from "../components/Reveal";

const FAQS = [
  {
    q: "Do you replace our receptionist?",
    a: "No. Leadtochair is built to support your front desk, not replace it. It handles structure and consistency — timely responses, follow-up, reminders — while your team keeps the judgement calls, clinical conversations and patient relationships.",
  },
  {
    q: "Do we need a new website?",
    a: "No. Leadtochair connects to your existing website's enquiry form and other channels — it doesn't require you to rebuild or replace your website.",
  },
  {
    q: "Does this work with our existing website?",
    a: "Yes. It's designed to connect to your existing enquiry channels rather than requiring a new site or platform.",
  },
  {
    q: "Does it work with our existing CRM/booking system?",
    a: "In most cases, yes. We review your current setup during onboarding and configure the connection accordingly. If something isn't directly compatible, we'll tell you before starting rather than after.",
  },
  {
    q: "What channels can be connected?",
    a: "Typically website forms, phone, WhatsApp, email, and where relevant your existing CRM or booking system. The exact combination is agreed with you before setup.",
  },
  {
    q: "How long does setup take?",
    a: "This depends on your current systems and channels — it's confirmed with you during the initial consultation rather than promised generically upfront.",
  },
  {
    q: "Can we see what is happening?",
    a: "Yes. You get visibility into enquiry volume, response times, bookings and recovered opportunities, so you can see the process working rather than taking it on trust.",
  },
  {
    q: "Is this designed around our existing process?",
    a: "Yes — the starting point is understanding how your practice currently handles enquiries, and building the structure around that rather than asking you to change how you work.",
  },
  {
    q: "What happens to patient data?",
    a: "Any implementation is configured together with your practice with appropriate data-protection requirements in mind. We're not a law firm and this isn't legal advice — if your practice has specific compliance requirements, those are factored into setup on a case-by-case basis.",
  },
];

export default function Faq() {
  const [open, setOpen] = useState(null);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="scroll-mt-24 border-t border-ink/10 py-24 md:py-32"
    >
      <div className="mx-auto max-w-3xl px-6">
        <Reveal>
          <Chapter number="10" label="Questions" />
        </Reveal>
        <Reveal delay={60}>
          <h2 className="font-display text-3xl font-medium leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Frequently asked questions
          </h2>
        </Reveal>

        <div className="mt-12">
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={item.q} delay={Math.min(i * 50, 250)}>
                <div data-testid={`faq-item-${i}`} className="border-b border-ink/10">
                  <button
                    data-testid={`faq-question-${i}`}
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-6 py-6 text-left"
                  >
                    <span className="font-medium tracking-wide text-ink md:text-lg">
                      {item.q}
                    </span>
                    <ChevronDown
                      size={20}
                      className={`shrink-0 text-teal transition-transform duration-300 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <div
                    className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
                      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p
                        data-testid={`faq-answer-${i}`}
                        className="max-w-2xl pb-6 text-sm leading-relaxed text-ink/70 md:text-base"
                      >
                        {item.a}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
